// SideTabs.js

import React, { useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Tab } from 'semantic-ui-react';
import Lowlight from 'react-lowlight';
import javascript from 'highlight.js/lib/languages/javascript';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Register the JavaScript language for syntax highlighting
Lowlight.registerLanguage('js', javascript);

// Inject CSS to ensure netlist links don't have unwanted pseudo-elements
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
        .netlist-model-link::before,
        .netlist-model-link::after {
            content: none !important;
        }
        .netlist-model-link {
            display: inline;
        }

        /* Flex layout for SideTabs: pin tab menu, scroll content */
        .side-tabs-flex {
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
            min-height: 0 !important;
        }
        .side-tabs-flex > .ui.secondary.pointing.menu {
            flex-shrink: 0 !important;
        }
        .side-tabs-flex > .ui.segment,
        .side-tabs-flex > .ui.tab.active,
        .side-tabs-flex > .ui.attached.segment.active.tab {
            flex: 1 1 auto !important;
            overflow-y: auto !important;
            min-height: 0 !important;
        }

        /* LED Color Picker Styles */
        .led-color-select option[value="red_1.8"] {
            background-color: #ff4444;
            color: white;
        }
        .led-color-select option[value="orange_2.2"] {
            background-color: #ff8800;
            color: white;
        }
        .led-color-select option[value="yellow_2.1"] {
            background-color: #ffcc00;
            color: #333;
        }
        .led-color-select option[value="green_2.0"] {
            background-color: #00cc00;
            color: white;
        }
        .led-color-select option[value="blue_3.5"] {
            background-color: #0066ff;
            color: white;
        }
        .led-color-select option[value="white_3.6"] {
            background-color: #ffffff;
            color: #333;
            border: 1px solid #ccc;
        }
    `;
    if (!document.getElementById('netlist-link-styles')) {
        style.id = 'netlist-link-styles';
        document.head.appendChild(style);
    }
}

function TextComponent(props) {
    const editorRef = useRef(null);

    useEffect(() => {
        if (props.description && editorRef.current) {
            const editor = editorRef.current.getEditor();
            if (editor) {
                const container = editor.container;
                const scrollElement = container.querySelector('.ql-editor');
                if (scrollElement) {
                    scrollElement.scrollTop = scrollElement.scrollHeight;
                }
            }
        }
    }, [props.description]);

    if (!props.description) {
        return <div></div>;
    }

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'indent'
    ];

    const modules = useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            ['clean']
        ],
        clipboard: {
            matchVisual: false
        },
        keyboard: {
            bindings: {}
        }
    }), []);

    return (
        <ReactQuill
            ref={editorRef}
            value={props.description}
            readOnly={true}
            theme="bubble"
            modules={modules}
            formats={formats}
        />
    );
}

function EditorComponent(props) {
    const quillRef = useRef(null);
    const saveButtonRef = useRef(null);
    const styleRef = useRef(null);

    useEffect(() => {
        if (saveButtonRef.current) {
            const button = saveButtonRef.current;
            if (props.show_save_buttons) {
                button.style.display = 'inline-block';
            } else {
                button.style.display = 'none';
            }
        }
    }, [props.show_save_buttons]);

    useEffect(() => {
        if (typeof window !== 'undefined' && !saveButtonRef.current) {
            const toolbar = document.querySelector('.ql-toolbar');
            if (!toolbar) return;

            const Quill = require('react-quill-new').Quill;

            // Add a save button to toolbar
            const saveButton = document.createElement('span');
            saveButtonRef.current = saveButton;
            saveButton.className = 'ql-formats';
            saveButton.style.display = props.show_save_buttons ? 'inline-block' : 'none';
            saveButton.innerHTML = `
                <button class="ql-save" type="button" data-tooltip="Save changes">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width: 18px; height: 18px; transform: scale(1.35);">
                        <path d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4z"></path>
                        <polyline points="17 3 17 7 7 7 7 3"></polyline>
                        <line x1="7" y1="13" x2="17" y2="13"></line>
                    </svg>
                </button>
            `;

            const button = saveButton.querySelector('button');
            if (button) {
                button.style.cssText = `
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 3px 5px;
                    height: 28px;
                    width: 32px;
                    color: var(--foreground);
                    background: none;
                    border: none;
                    cursor: pointer;
                    border-radius: 3px;
                    position: relative;
                `;
                button.addEventListener('mouseover', () => {
                    button.style.background = 'var(--muted)';
                });
                button.addEventListener('mouseout', () => {
                    button.style.background = 'none';
                });
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (props.onSave) {
                        props.onSave();
                    }
                });
            }

            toolbar.appendChild(saveButton);

            // Add custom CSS to ensure proper spacing and tooltip
            const style = document.createElement('style');
            styleRef.current = style;
            style.textContent = `
                .ql-toolbar.ql-snow {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 5px;
                    padding: 8px;
                }
                .ql-editor {
                    white-space: pre-wrap !important;
                }
                .ql-toolbar .ql-formats {
                    margin-right: 5px !important;
                }
                .ql-toolbar .ql-formats:last-child {
                    margin-right: 0 !important;
                    margin-left: auto !important;
                }
                .ql-save:hover {
                    color: #06c !important;
                }
                .ql-save svg {
                    position: relative;
                    top: -1px;
                }
                [data-tooltip] {
                    position: relative;
                }
                [data-tooltip]:hover:after {
                    content: attr(data-tooltip);
                    position: absolute;
                    bottom: -30px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #2c2c2c;
                    color: white;
                    padding: 5px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    white-space: nowrap;
                    z-index: 1000;
                }
                [data-tooltip]:hover:before {
                    content: '';
                    position: absolute;
                    bottom: -10px;
                    left: 50%;
                    transform: translateX(-50%);
                    border-width: 5px;
                    border-style: solid;
                    border-color: transparent transparent #2c2c2c transparent;
                    z-index: 1000;
                }
            `;
            document.head.appendChild(style);
        }

        // Cleanup function
        return () => {
            if (saveButtonRef.current) {
                saveButtonRef.current.remove();
                saveButtonRef.current = null;
            }
            if (styleRef.current) {
                styleRef.current.remove();
                styleRef.current = null;
            }
        };
    }, []); // Empty dependency array since we only want this to run once on mount

    const modules = useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['clean']
        ]
    }), []);

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'indent'
    ];

    const handleChange = (content, delta, source, editor) => {
        props.setDescription(content);
    };

    const wrapperRef = useRef(null);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (wrapper) {
            const stopProp = (e) => e.stopPropagation();
            const setDisableFocus = () => { window.disable_haxe_events = true; };
            const setDisableBlur = () => { window.disable_haxe_events = false; };

            wrapper.addEventListener('keydown', stopProp);
            wrapper.addEventListener('keyup', stopProp);
            wrapper.addEventListener('keypress', stopProp);
            wrapper.addEventListener('focusin', setDisableFocus);
            wrapper.addEventListener('focusout', setDisableBlur);

            return () => {
                wrapper.removeEventListener('keydown', stopProp);
                wrapper.removeEventListener('keyup', stopProp);
                wrapper.removeEventListener('keypress', stopProp);
                wrapper.removeEventListener('focusin', setDisableFocus);
                wrapper.removeEventListener('focusout', setDisableBlur);
            };
        }
    }, []);

    return (
        <div ref={wrapperRef}>
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={props.description || ''}
                modules={modules}
                formats={formats}
                onChange={handleChange}
                readOnly={false}
                preserveWhitespace={true}
            />
        </div>
    );
}

// Helper function to get localized string from window.l (lab_translations)
const lt = (key, defaultValue = '') => {
    try {
        const keys = key.split('.');
        let value = typeof window !== 'undefined' && window.l;
        for (const k of keys) {
            if (value && typeof value === 'object') {
                value = value[k];
            } else {
                value = null;
                break;
            }
        }
        return value || defaultValue;
    } catch (e) {
        return defaultValue;
    }
};

// Make lt and editNGSpiceNetlistLine available globally for Haxe/JavaScript calls
if (typeof window !== 'undefined') {
    window.lt = lt;
    window.editNGSpiceNetlistLine = function(modelJson) {
        try {
            const modelData = typeof modelJson === 'string' ? JSON.parse(modelJson) : modelJson;
            console.log('Editing NGSpice model:', modelData);

            if (window.JsonEditorHelper) {
                // Check if this is an LED - use enum for color picker
                const isLED = modelData.type === 'LED';

                // Define LED color options (matching Haxe Led.hx)
                // Using enum values that map to voltages
                const ledColorEnumValues = ['red_1.8', 'orange_2.2', 'yellow_2.1', 'green_2.0', 'blue_3.5', 'white_3.6'];
                // Use localized labels from window.l.spice_input.led_colors, falling back to Haxe JSON colorOptions (via Main.word()), then English
                const ledColorEnumNames = isLED && window.l && window.l.spice_input && window.l.spice_input.led_colors
                    ? window.l.spice_input.led_colors
                    : isLED && modelData.colorOptions
                    ? modelData.colorOptions.map(o => o.label)
                    : ['Red 1.8V', 'Orange 2.2V', 'Yellow 2.1V', 'Green 2.0V', 'Blue 3.5V', 'White 3.6V'];

                // Determine current color selection from color name
                let currentColorIndex = 0;
                if (isLED && modelData.color) {
                    const colorIndexMap = { red: 0, orange: 1, yellow: 2, green: 3, blue: 4, white: 5 };
                    currentColorIndex = colorIndexMap[modelData.color] ?? 0;
                }

                // Filter out non-editable metadata keys
                const metadataKeys = ['componentId', 'componentType', 'modelName', 'type', 'labels', 'ranges', 'descriptions', 'color', 'colorInt', 'colorOptions', 'level'];

                // Build parameter definitions with labels, ranges, and descriptions from the JSON
                const params = [];
                const data = {};

                // For LED, add color picker first using enum
                if (isLED) {
                    params.push({
                        name: '_ledColor',
                        type: 'string',
                        title: (modelData.labels && modelData.labels.color) || window.lt('led.color', 'LED Color'),
                        default: ledColorEnumValues[currentColorIndex],
                        enum: ledColorEnumValues,
                        enumNames: ledColorEnumNames,
                        description: (modelData.descriptions && modelData.descriptions.color) || window.lt('led.color_desc', 'Select LED color to set forward voltage drop'),
                        options: {
                            className: 'led-color-select'
                        }
                    });
                    data['_ledColor'] = ledColorEnumValues[currentColorIndex];
                }

                for (const key in modelData) {
                    if (!metadataKeys.includes(key)) {
                        const value = modelData[key];
                        const label = modelData.labels && modelData.labels[key] ? modelData.labels[key] : key.toUpperCase();
                        const description = modelData.descriptions && modelData.descriptions[key] ? modelData.descriptions[key] : null;
                        const range = modelData.ranges && modelData.ranges[key] ? modelData.ranges[key] : null;

                        // Always use 'number' type for SPICE parameters to preserve floating point decimals
                        let paramType = 'number';

                        const paramDef = {
                            name: key,
                            type: paramType,
                            title: label,
                            default: value,
                            step: 'any'  // Allow decimal input
                        };

                        // Add description if available
                        if (description) {
                            paramDef.description = description;
                        }

                        // Add range constraints if available
                        if (range) {
                            if (range.min !== undefined) paramDef.min = range.min;
                            if (range.max !== undefined) paramDef.max = range.max;
                            if (range.step !== undefined) paramDef.step = range.step;
                        }

                        params.push(paramDef);
                        data[key] = value;
                    }
                }

                // Open the JSON editor with custom labels and ranges
                window.JsonEditorHelper.openSimple(
                    `${window.lt('spice_input.edit', 'Edit')} ${modelData.modelName} (${modelData.type})`,
                    params,
                    data,
                    function(result) {
                        console.log('Updated model parameters:', result);

                        // For LED, map color selection to fwdrop voltage and colorInt hex
                        if (isLED && result._ledColor) {
                            const colorMap = {
                                'red_1.8': 1.8,
                                'orange_2.2': 2.2,
                                'yellow_2.1': 2.1,
                                'green_2.0': 2.0,
                                'blue_3.5': 3.5,
                                'white_3.6': 3.6
                            };
                            const colorIntMap = {
                                'red_1.8': 0xFF0000,
                                'orange_2.2': 0xFF5B0D,
                                'yellow_2.1': 0xF2F200,
                                'green_2.0': 0x17FF8B,
                                'blue_3.5': 0x1E83FE,
                                'white_3.6': 0xFFFFFF
                            };
                            result.fwdrop = colorMap[result._ledColor] || result.fwdrop;
                            result._newColorInt = colorIntMap[result._ledColor];
                            delete result._ledColor; // Remove the helper field
                        }

                        // Show what changed
                        const changes = [];
                        for (const key in result) {
                            if (modelData[key] !== undefined && modelData[key] !== result[key]) {
                                const label = modelData.labels && modelData.labels[key] ? modelData.labels[key] : key.toUpperCase();
                                changes.push(`${label}: ${modelData[key]} → ${result[key]}`);
                            }
                        }

                        if (changes.length > 0) {
                            toast.info(
                                <div style={{ textAlign: 'left' }}>
                                    <strong>Updated {modelData.modelName}</strong><br />
                                    {changes.map((change, i) => <div key={i}>{change}</div>)}
                                </div>,
                                {
                                    position: toast.POSITION.TOP_RIGHT,
                                    autoClose: 2000
                                }
                            );
                        }

                        // Call Haxe function to update the component properties
                        // Generic function that uses reflection to set any property
                        if (typeof com !== 'undefined' && com.clientside && com.clientside.main.NGSpice) {
                            com.clientside.main.Html5Bootstrap.jsCall("updateComponentProperties", {
                                componentId: modelData.componentId,
                                params: result
                            });
                        } else if (window.updateComponentProperties) {
                            window.updateComponentProperties(modelData.componentId, result);
                        }
                    }
                );
            } else {
                // Fallback if JsonEditorHelper is not available
                const paramList = Object.keys(modelData)
                    .filter(k => !['componentId', 'componentType', 'modelName', 'type', 'labels', 'ranges', 'descriptions', 'color', 'colorInt'].includes(k))
                    .map(k => {
                        const label = modelData.labels && modelData.labels[k] ? modelData.labels[k] : k.toUpperCase();
                        return `${label}: ${modelData[k]}`;
                    })
                    .join(', ');

                toast.info(
                    <div style={{ textAlign: 'left' }}>
                        <strong>Model: {modelData.modelName}</strong><br />
                        Type: {modelData.type}<br />
                        {paramList}<br />
                        <small>JsonEditorHelper not loaded</small>
                    </div>,
                    {
                        position: toast.POSITION.TOP_RIGHT,
                        autoClose: 5000,
                        closeButton: true
                    }
                );
            }

            // Store for future editing
            window.currentEditingModel = modelData;

        } catch (e) {
            console.error('Error parsing model JSON:', e);
            toast.error('Error loading model data');
        }
    };
}

// NetlistViewer component that renders netlist with clickable hyperlinks
function NetlistViewer(props) {
    const { netlist, modelJsons, selectedComponentId } = props;
    const containerRef = useRef(null);
    const previousSelectedIdRef = useRef(null);

    // Parse the netlist and convert [[LINK:id|json]]markup to clickable links
    // The link markup is followed by the actual model line, e.g., [[LINK:id|json]].model NMOSFET99...
    const renderNetlist = () => {
        if (!netlist) return [];

        // Regular expression to match [[LINK:modelName|jsonData]] followed by the model line
        // Captures: [1]=linkId, [2]=jsonData, [3]=modelLine (up to newline)
        // Using [\s\S] instead of . to match any character including newline
        const linkRegex = /\[\[LINK:([^\|]+)\|([^\]]+)\]\]([^\n]*)(\n)?/g;

        // Split by the link pattern while preserving the matches
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = linkRegex.exec(netlist)) !== null) {
            // Add the text before the link
            if (match.index > lastIndex) {
                parts.push({
                    type: 'text',
                    content: netlist.substring(lastIndex, match.index)
                });
            }

            // Add the link
            const linkId = match[1];
            const jsonData = match[2];
            const modelLine = match[3]; // The actual model line text
            const newlineAfter = match[4] || ''; // Preserve the newline if present

            try {
                // Parse the JSON to get model info
                const modelData = JSON.parse(jsonData);
                parts.push({
                    type: 'link',
                    linkId: linkId,
                    modelData: modelData,
                    displayText: modelLine,
                    newlineAfter: newlineAfter  // Store newline to add after the link
                });
            } catch (e) {
                // If JSON parsing fails, treat as text
                parts.push({
                    type: 'text',
                    content: match[0]
                });
            }

            lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < netlist.length) {
            parts.push({
                type: 'text',
                content: netlist.substring(lastIndex)
            });
        }

        return parts;
    };

    const netlistParts = useMemo(() => renderNetlist(), [netlist, modelJsons]);

    const handleLinkClick = (modelData) => {
        if (modelData._infoOnly) {
            return;
        }
        if (modelData._selectOnly) {
            // Selection-only link: highlight the component on the board
            if (typeof com !== 'undefined' && com.clientside && com.clientside.main.Html5Bootstrap) {
                com.clientside.main.Html5Bootstrap.jsCall("selectComponent", modelData.componentId, null);
            }
        } else if (window.editNGSpiceNetlistLine) {
            window.editNGSpiceNetlistLine(modelData);
        }
    };

    // Scroll to highlighted component when selectedComponentId changes
    useEffect(() => {
        // Skip on initial mount (when previousId is still null)
        if (previousSelectedIdRef.current === null) {
            previousSelectedIdRef.current = selectedComponentId;
            return;
        }

        if (selectedComponentId >= 0 && containerRef.current) {
            // Find the highlighted link by data-component-id attribute
            const highlightedLink = containerRef.current.querySelector(`[data-component-id="${selectedComponentId}"]`);
            if (highlightedLink) {
                highlightedLink.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            }
        }
        previousSelectedIdRef.current = selectedComponentId;
    }, [selectedComponentId]);

    return (
        <React.Fragment>
        <style>{`
            @keyframes netlist-highlight-pulse {
                0%, 100% { box-shadow: 0 0 4px rgba(0, 102, 204, 0.3); }
                50% { box-shadow: 0 0 10px rgba(0, 102, 204, 0.6); }
            }
        `}</style>
        <pre
            ref={containerRef}
            style={{
            backgroundColor: 'var(--muted)',
            color: 'var(--muted-foreground)',
            padding: '10px',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '13px',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all'
        }}>
            {Array.isArray(netlistParts) && netlistParts.length > 0 ? (
                netlistParts.map((part, index) => {
                    if (part.type === 'link') {
                        return (
                            <React.Fragment key={index}>
                                <span
                                    data-component-id={part.modelData.componentId}
                                    onClick={() => handleLinkClick(part.modelData)}
                                    style={{
                                        color: part.modelData._infoOnly ? '#707070' : '#0066cc',
                                        cursor: part.modelData._infoOnly ? 'help' : 'pointer',
                                        textDecoration: 'none',
                                        fontStyle: part.modelData._infoOnly ? 'italic' : 'normal',
                                        ...(selectedComponentId >= 0 && part.modelData.componentId === selectedComponentId ? {
                                            backgroundColor: 'rgba(0, 102, 204, 0.15)',
                                            borderRadius: '3px',
                                            padding: '1px 4px',
                                            boxShadow: '0 0 6px rgba(0, 102, 204, 0.4)',
                                            animation: 'netlist-highlight-pulse 1.5s ease-in-out infinite'
                                        } : {})
                                    }}
                                    className={`netlist-model-link${selectedComponentId >= 0 && part.modelData.componentId === selectedComponentId ? ' netlist-link-selected' : ''}`}
                                    title={(() => {
                                        const md = part.modelData;
                                        if (md._tooltip) {
                                            return md._tooltip;
                                        }
                                        if (md._selectOnly) {
                                            return `Click to select ${md.componentType} on board`;
                                        }
                                        const metaKeys = ['componentId','componentType','modelName','type','labels','ranges','descriptions','color','colorInt','colorOptions','_selectOnly','_infoOnly','_tooltip'];
                                        const params = Object.keys(md)
                                            .filter(k => !metaKeys.includes(k))
                                            .map(k => {
                                                const label = md.labels && md.labels[k] ? md.labels[k] : k;
                                                return `${label}: ${md[k]}`;
                                            })
                                            .join(', ');
                                        return `Click to edit ${md.modelName} (${params})`;
                                    })()}
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                >
                                    {part.displayText}
                                </span>
                                {part.newlineAfter}
                            </React.Fragment>
                        );
                    }
                    const lines = part.content.split('\n');
                    if (lines.length <= 1) {
                        const isComment = part.content.trimStart().startsWith('*');
                        return <span key={index} style={isComment ? { color: 'var(--muted-foreground)', fontStyle: 'italic' } : {}}>{part.content}</span>;
                    }
                    return <React.Fragment key={index}>
                        {lines.map((line, li) => {
                            const isComment = line.trimStart().startsWith('*');
                            return <span key={li} style={isComment ? { color: 'var(--muted-foreground)', fontStyle: 'italic' } : {}}>{line}{li < lines.length - 1 ? '\n' : ''}</span>;
                        })}
                    </React.Fragment>;
                })
            ) : (
                <span>{netlist || ''}</span>
            )}
        </pre>
        </React.Fragment>
    );
}

function SideTabs(props) {
    useEffect(() => {
        if (props.isStudentAnswering) {
            props.setVisible(true);
        }
    }, [props.isStudentAnswering, props.setVisible]);

    let panes = [];

    if (props.isStudentAnswering) {

        panes.push({
            menuItem: l.question,
            render: () => (
                <Tab.Pane attached={false}>
                    <TextComponent description={props.question_description} />
                </Tab.Pane>
            ),
        });

        panes.push({
            menuItem: l.submit_answer,
            render: () => (
                <Tab.Pane attached={false}>
                    <EditorComponent
                        editable={true}
                        description={props.description}
                        setDescription={props.setDescription}
                        onSave={props.onSave}
                        show_save_buttons={props.show_save_buttons}
                    />
                </Tab.Pane>
            ),
        });
    } else if (props.isEditingAssignment) {
        panes.push({
            menuItem: l.question,
            render: () => (
                <Tab.Pane attached={false}>
                    <EditorComponent
                        editable={true}
                        description={props.description}
                        setDescription={props.setDescription}
                        onSave={props.onSave}
                        show_save_buttons={props.show_save_buttons}
                    />
                </Tab.Pane>
            ),
        });
    } else {
        // This is a note!
        if (props.isCreator) {
            panes.push({
                menuItem: l.notes,
                render: () => (
                    <Tab.Pane attached={false}>
                        <EditorComponent
                            editable={true}
                            description={props.description}
                            setDescription={props.setDescription}
                            onSave={props.onSave}
                            show_save_buttons={props.show_save_buttons}
                        />
                    </Tab.Pane>
                ),
            });
        } else {
            panes.push({
                menuItem: l.notes,
                render: () => (
                    <Tab.Pane attached={false}>
                        {props.labPageNotSigned || props.isCreator ? (
                            <EditorComponent
                                editable={true}
                                description={props.description}
                                setDescription={props.setDescription}
                                onSave={props.onSave}
                                show_save_buttons={props.show_save_buttons}
                            />
                        ) : (
                            <TextComponent description={props.description} />
                        )}
                    </Tab.Pane>
                ),
            });
        }

        panes.push({
            menuItem: 'NGSPice',
            render: () => (
                <Tab.Pane style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }} attached={false}>
                    <NetlistViewer netlist={props.NGSpiceNetList} modelJsons={props.ngSpiceModelJsons} selectedComponentId={props.selectedComponentId} />
                </Tab.Pane>
            ),
        });
    }

    return props.useNGSpice || props.isEditingAssignment || props.isStudentAnswering ? (
        <Tab
            className="side-tabs-flex"
            style={{ width: '100%', flex: 1, minHeight: 0 }}
            defaultActiveIndex={props.ngSpiceSelected ? 1 : 0}
            menu={{ secondary: true, pointing: true }}
            panes={panes}
        />
    ) : props.isCreator || props.labPageNotSigned ? (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', width: '100%' }}>
            <EditorComponent
                editable={true}
                description={props.description}
                setDescription={props.setDescription}
                onSave={props.onSave}
                show_save_buttons={props.show_save_buttons}
            />
        </div>
    ) : (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', width: '100%' }}>
            <TextComponent description={props.description} />
        </div>
    );
}

export default SideTabs;
